import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, ChevronUp, ChevronDown, GripVertical, Eye, EyeOff, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import ImageUpload from "./ImageUpload";
import VideoUpload from "./VideoUpload";
import RichTextEditor from "./RichTextEditor";
import { isHtml, shorthandToHtml } from "../../lib/richText";

const NAV_PARENTS = [
  { value: "none", label: "Top-level menu item" },
  { value: "about", label: "Under: About Us" },
  { value: "services", label: "Under: Services" },
  { value: "more", label: "Under: More Than Numbers" },
];

const BLOCK_TYPES = [
  { value: "hero", label: "Hero banner" },
  { value: "text", label: "Text section" },
  { value: "imageText", label: "Image / Video + Text" },
  { value: "image", label: "Image only" },
  { value: "video", label: "Video" },
  { value: "cards", label: "Cards grid" },
  { value: "cta", label: "Call to action" },
];

const COLOR_PRESETS = [
  { label: "Sky", value: "#2EA3FD" },
  { label: "Navy", value: "#0B1A2F" },
  { label: "Orange", value: "#F26A21" },
  { label: "Teal", value: "#0EA5A5" },
  { label: "Green", value: "#16A34A" },
];

// Section background options — drawn from the site palette so pages match the rest of the website.
const BG_OPTIONS = [
  { value: "white", label: "White" },
  { value: "light", label: "Light grey" },
  { value: "navy", label: "Navy (dark)" },
];
const bgField = { name: "bg", label: "Section background", type: "select", options: BG_OPTIONS };

// Form presets (added via the "Add form" buttons).
const FORM_PRESETS = {
  contact: {
    type: "form", heading: "Get in touch",
    intro: "Tell us a bit about your business and we'll be in touch.",
    fields: { name: true, email: true, phone: true, business: true, message: true },
    buttonText: "Send enquiry", buttonColor: "#2EA3FD",
    successMessage: "Thanks — we'll be in touch shortly.",
  },
  callback: {
    type: "form", heading: "Request a callback",
    intro: "Leave your details and a good time to call.",
    fields: { name: true, email: true, phone: true, message: true },
    buttonText: "Request callback", buttonColor: "#2EA3FD",
    successMessage: "Thanks — we'll call you back soon.",
  },
};

const FORM_FIELD_KEYS = [
  { key: "name", label: "Name", locked: true },
  { key: "email", label: "Email", locked: true },
  { key: "phone", label: "Phone" },
  { key: "business", label: "Business name" },
  { key: "message", label: "Message" },
];

const BLOCK_FIELDS = {
  hero: [
    { name: "eyebrow", label: "Eyebrow (small label)", type: "text" },
    { name: "heading", label: "Heading", type: "text" },
    { name: "subheading", label: "Subheading", type: "textarea", rows: 2 },
    { name: "buttonText", label: "Button text", type: "text" },
    { name: "buttonLink", label: "Button link (e.g. /contact)", type: "text" },
    { name: "buttonColor", label: "Button colour", type: "color" },
  ],
  text: [
    { name: "heading", label: "Heading (optional — leave blank to hide)", type: "text" },
    { name: "body", label: "Body", type: "richtext" },
    bgField,
  ],
  imageText: [
    { name: "heading", label: "Heading (optional)", type: "text" },
    { name: "body", label: "Body", type: "richtext" },
    { name: "image", label: "Image", type: "image", hint: "Used unless you add a video below — a video always takes priority." },
    { name: "video", label: "Or upload a video instead", type: "video", hint: "MP4 or MOV, up to 300MB. Shown in place of the image." },
    { name: "videoUrl", label: "Or paste a YouTube / Vimeo link", type: "text", hint: "e.g. https://www.youtube.com/watch?v=… — only used when no video is uploaded above." },
    { name: "poster", label: "Video cover image (optional)", type: "image", hint: "Shown before the video plays. Leave blank to use a frame from the video." },
    { name: "imagePosition", label: "Image / video position", type: "select", options: [{ value: "left", label: "Left" }, { value: "right", label: "Right" }] },
    { name: "buttonText", label: "Button text", type: "text" },
    { name: "buttonLink", label: "Button link", type: "text" },
    { name: "buttonColor", label: "Button colour", type: "color" },
    bgField,
  ],
  image: [
    { name: "image", label: "Image", type: "image" },
    { name: "caption", label: "Caption (optional)", type: "text" },
    { name: "width", label: "Width", type: "select", options: [{ value: "wide", label: "Wide" }, { value: "narrow", label: "Narrow (centred)" }] },
    bgField,
  ],
  video: [
    { name: "heading", label: "Heading (optional)", type: "text" },
    { name: "video", label: "Upload a video", type: "video", hint: "MP4 or MOV, up to 300MB. Leave blank if you'd rather paste a YouTube or Vimeo link below." },
    { name: "videoUrl", label: "Or paste a YouTube / Vimeo link", type: "text", hint: "e.g. https://www.youtube.com/watch?v=… — only used when no video is uploaded above." },
    { name: "poster", label: "Cover image (optional)", type: "image", hint: "Shown before the video plays. Leave blank to use a frame from the video." },
    { name: "caption", label: "Caption (optional)", type: "text" },
    { name: "width", label: "Width", type: "select", options: [{ value: "wide", label: "Wide" }, { value: "narrow", label: "Narrow (centred)" }] },
    bgField,
  ],
  cards: [
    { name: "heading", label: "Heading (optional)", type: "text" },
    { name: "cards", label: "Cards", type: "cards" },
    bgField,
  ],
  cta: [
    { name: "heading", label: "Heading (optional)", type: "text" },
    { name: "text", label: "Text", type: "textarea", rows: 2 },
    { name: "buttonText", label: "Button text", type: "text" },
    { name: "buttonLink", label: "Button link", type: "text" },
    { name: "buttonColor", label: "Button colour", type: "color" },
    bgField,
  ],
  form: [
    { name: "heading", label: "Form heading", type: "text" },
    { name: "intro", label: "Intro text (optional)", type: "textarea", rows: 2 },
    { name: "fields", label: "Fields to show", type: "formfields" },
    { name: "buttonText", label: "Button text", type: "text" },
    { name: "buttonColor", label: "Button colour", type: "color" },
    { name: "successMessage", label: "Thank-you message", type: "textarea", rows: 2 },
    bgField,
  ],
};

const emptyPage = () => ({ title: "", slug: "", nav_parent: "none", nav_label: "", show_in_nav: true, published: false, go_live: "", blocks: [] });

function BlockEditor({ blocks, setBlocks }) {
  const update = (idx, key, value) => setBlocks(blocks.map((b, i) => (i === idx ? { ...b, [key]: value } : b)));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const arr = [...blocks];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setBlocks(arr);
  };
  const remove = (idx) => setBlocks(blocks.filter((_, i) => i !== idx));
  const add = (type) => setBlocks([...blocks, { type }]);

  const cardsToText = (cards = []) => cards.map((c) => `${c.title || ""} :: ${c.text || ""}`).join("\n");
  const textToCards = (txt) => txt.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [title, ...rest] = l.split("::");
    return { title: (title || "").trim(), text: rest.join("::").trim() };
  });

  return (
    <div className="space-y-4">
      {blocks.map((block, idx) => (
        <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50/60">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 text-sm font-bold text-navy">
              <GripVertical size={15} className="text-slate-400" />
              {BLOCK_TYPES.find((t) => t.value === block.type)?.label || block.type}
            </span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => move(idx, -1)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-navy"><ChevronUp size={15} /></button>
              <button type="button" onClick={() => move(idx, 1)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-navy"><ChevronDown size={15} /></button>
              <button type="button" onClick={() => remove(idx)} className="w-8 h-8 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
          <div className="space-y-3">
            {BLOCK_FIELDS[block.type]?.map((f) => (
              <div key={f.name}>
                <Label className="text-navy text-sm font-semibold">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea rows={f.rows || 3} value={block[f.name] || ""} onChange={(e) => update(idx, f.name, e.target.value)} className="mt-1.5 bg-white" />
                ) : f.type === "richtext" ? (
                  <div className="mt-1.5">
                    <RichTextEditor
                      value={isHtml(block[f.name]) ? block[f.name] : shorthandToHtml(block[f.name] || "")}
                      onChange={(html) => update(idx, f.name, html)}
                    />
                  </div>
                ) : f.type === "color" ? (
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <input type="color" value={block[f.name] || "#2EA3FD"} onChange={(e) => update(idx, f.name, e.target.value)} className="h-9 w-12 rounded border border-slate-200 bg-white cursor-pointer" />
                    <input type="text" value={block[f.name] || "#2EA3FD"} onChange={(e) => update(idx, f.name, e.target.value)} className="h-9 w-28 rounded-lg border border-slate-200 px-2 text-sm font-mono bg-white" placeholder="#2EA3FD" />
                    {COLOR_PRESETS.map((c) => (
                      <button key={c.value} type="button" onClick={() => update(idx, f.name, c.value)} title={c.label} className="h-6 w-6 rounded-full border border-white shadow ring-1 ring-slate-200" style={{ backgroundColor: c.value }} />
                    ))}
                  </div>
                ) : f.type === "formfields" ? (
                  <div className="mt-1.5 flex flex-wrap gap-3">
                    {FORM_FIELD_KEYS.map((fk) => {
                      const fields = block.fields || { name: true, email: true, phone: true, message: true };
                      const on = fk.locked ? true : !!fields[fk.key];
                      return (
                        <label key={fk.key} className={`flex items-center gap-1.5 text-sm ${fk.locked ? "text-slate-400" : "text-navy cursor-pointer"}`}>
                          <input type="checkbox" checked={on} disabled={fk.locked} onChange={(e) => update(idx, "fields", { ...fields, [fk.key]: e.target.checked })} className="w-4 h-4 accent-orange" />
                          {fk.label}{fk.locked ? " (always)" : ""}
                        </label>
                      );
                    })}
                  </div>
                ) : f.type === "select" ? (
                  <Select value={block[f.name] || f.options[0].value} onValueChange={(v) => update(idx, f.name, v)}>
                    <SelectTrigger className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "image" ? (
                  <div className="mt-1.5"><ImageUpload value={block[f.name] || ""} onChange={(v) => update(idx, f.name, v)} /></div>
                ) : f.type === "video" ? (
                  <div className="mt-1.5" data-testid="page-block-video-upload"><VideoUpload value={block[f.name] || ""} onChange={(v) => update(idx, f.name, v)} /></div>
                ) : f.type === "cards" ? (
                  <>
                    <Textarea rows={4} value={cardsToText(block.cards)} onChange={(e) => update(idx, "cards", textToCards(e.target.value))} className="mt-1.5 bg-white" placeholder="One card per line, format:  Title :: Description" />
                    <p className="text-xs text-slate-400 mt-1">One card per line, using <code>Title :: Description</code></p>
                  </>
                ) : (
                  <Input value={block[f.name] || ""} onChange={(e) => update(idx, f.name, e.target.value)} className="mt-1.5 bg-white" />
                )}
                {f.hint && <p className="text-xs text-slate-400 mt-1">{f.hint}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2 pt-1">
        {BLOCK_TYPES.map((t) => (
          <button key={t.value} type="button" onClick={() => add(t.value)} className="text-xs font-semibold px-3 py-2 rounded-full bg-navy/5 text-navy hover:bg-navy hover:text-white transition-colors">
            <Plus size={13} className="inline mr-1" />{t.label}
          </button>
        ))}
        <button type="button" onClick={() => setBlocks([...blocks, { ...FORM_PRESETS.contact }])} className="text-xs font-semibold px-3 py-2 rounded-full bg-orange/10 text-orange hover:bg-orange hover:text-white transition-colors">
          <Plus size={13} className="inline mr-1" />Form: Get in touch
        </button>
        <button type="button" onClick={() => setBlocks([...blocks, { ...FORM_PRESETS.callback }])} className="text-xs font-semibold px-3 py-2 rounded-full bg-orange/10 text-orange hover:bg-orange hover:text-white transition-colors">
          <Plus size={13} className="inline mr-1" />Form: Request a callback
        </button>
      </div>
    </div>
  );
}

export default function PagesManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPage());
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [navParents, setNavParents] = useState(NAV_PARENTS);

  useEffect(() => {
    // Menu locations come from the Navigation manager so custom dropdowns appear here too.
    adminApi.navigation.groups()
      .then((gs) => setNavParents([{ value: "none", label: "Top-level menu item" }, ...gs.map((g) => ({ value: g.id, label: `Under: ${g.label}` }))]))
      .catch(() => {});
  }, []);

  const load = () => { setLoading(true); adminApi.pages.list().then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openCreate = () => { setEditing(null); setForm(emptyPage()); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyPage(), ...it }); setOpen(true); };

  const handleSave = async () => {
    if (!form.title) { toast({ title: "Please add a title", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editing) await adminApi.pages.update(editing.id, form);
      else await adminApi.pages.create(form);
      toast({ title: `Page ${editing ? "updated" : "created"}` });
      setOpen(false); load();
    } catch (e) { toast({ title: "Error saving", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminApi.pages.remove(deleteId); toast({ title: "Page deleted" }); load(); }
    catch { toast({ title: "Error deleting", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Pages</h1>
          <p className="text-sm text-slate-500">{items.length} pages • build custom pages from section blocks</p>
        </div>
        <Button onClick={openCreate} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Plus size={16} className="mr-1.5" /> New Page</Button>
      </div>

      {loading ? <p className="text-slate-400">Loading...</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">No pages yet. Create your first custom page!</div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate flex items-center gap-2">
                  {p.title}
                  {p.published ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-amber-500" />}
                </p>
                <p className="text-sm text-slate-400 truncate">
                  /p/{p.slug} • {navParents.find((n) => n.value === (p.nav_parent || "none"))?.label || "Not in menu"}
                  {p.go_live ? ` • live from ${p.go_live}` : ""}
                </p>
              </div>
              <a href={`/admin/pages/${p.id}/preview`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy" title="Preview (works even if unpublished)"><ExternalLink size={15} /></a>
              <button onClick={() => openEdit(p)} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy"><Pencil size={15} /></button>
              <button onClick={() => setDeleteId(p.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Page" : "New Page"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-navy font-semibold">Page title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-2" placeholder="e.g. Our Guarantee" /></div>
              <div><Label className="text-navy font-semibold">URL slug (optional)</Label><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="mt-2" placeholder="auto-generated from title" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-navy font-semibold">Where in the menu?</Label>
                <Select value={form.nav_parent} onValueChange={(v) => set("nav_parent", v)}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{navParents.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-navy font-semibold">Menu label (optional)</Label><Input value={form.nav_label} onChange={(e) => set("nav_label", e.target.value)} className="mt-2" placeholder="Defaults to the title" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-navy font-semibold">Go-live date (optional)</Label>
                <Input type="date" value={form.go_live ? form.go_live.slice(0, 10) : ""} onChange={(e) => set("go_live", e.target.value)} className="mt-2" />
                <p className="text-xs text-slate-400 mt-1">Page stays hidden until this date. Leave blank to go live immediately (once published).</p>
              </div>
              <div className="flex flex-col gap-3 justify-center">
                <div className="flex items-center justify-between"><Label className="text-navy font-semibold">Published</Label><Switch checked={!!form.published} onCheckedChange={(v) => set("published", v)} /></div>
                <div className="flex items-center justify-between"><Label className="text-navy font-semibold">Show in navigation menu</Label><Switch checked={!!form.show_in_nav} onCheckedChange={(v) => set("show_in_nav", v)} /></div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <Label className="text-navy font-semibold text-base">Page content (section blocks)</Label>
              <p className="text-xs text-slate-400 mb-3">Add, reorder and edit blocks to build your page.</p>
              <BlockEditor blocks={form.blocks || []} setBlocks={(b) => set("blocks", b)} />
            </div>
          </div>
          <DialogFooter>
            {editing && (
              <Button variant="outline" onClick={() => window.open(`/admin/pages/${editing.id}/preview`, "_blank")} className="rounded-full mr-auto" data-testid="page-preview-btn">
                <Eye size={15} className="mr-1" /> Preview
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save Page"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this page?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
